import { InfoIcon, InformationalBanner, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { routes } from '../../../config/routes';

export default function TuitionConversionBanner({
  billingInterval,
  conversionSummaries,
  financialSummary,
  isManualOverride,
  mathNote,
  tuitionSettingsUrl,
}) {
  const { t } = useTranslation();
  if (!conversionSummaries.length && !mathNote && !financialSummary) return null;

  return (
    <InformationalBanner
      label={
        <div>
          {conversionSummaries.length > 0 ? (
            <div className="grid gap-3">
              {conversionSummaries.map((summary, summaryIndex) => (
                <TuitionConversionSummary
                  key={
                    summary.invoiceItemId ??
                    `${summary.studentId ?? 'legacy'}-${summary.invoiceItemIndex ?? summaryIndex}`
                  }
                  billingInterval={billingInterval}
                  showStudent={conversionSummaries.length > 1}
                  summary={summary}
                  t={t}
                  tuitionSettingsUrl={tuitionSettingsUrl}
                />
              ))}
            </div>
          ) : mathNote ? (
            <div>{mathNote}</div>
          ) : null}
          <ConversionGuidance t={t} tuitionSettingsUrl={tuitionSettingsUrl} />
          {financialSummary && (
            <FinancialSummary financialSummary={financialSummary} isManualOverride={isManualOverride} t={t} />
          )}
          {conversionSummaries.length > 0 && (
            <div className="mt-3" data-testid="tuition-future-invoices-note">
              {t('billing.tuitionAutofill.futureInvoicesOnly', { defaultValue: 'Existing invoices will not change.' })}
            </div>
          )}
        </div>
      }
      type="info"
      size={WidgetSizeEnum.SMALL}
      extraClasses="mb-4 text-sm"
      icon={<InfoIcon className="size-5" />}
      data-testid="tuition-autofill-math-note"
    />
  );
}

function TuitionConversionSummary({ billingInterval, showStudent, summary, t, tuitionSettingsUrl }) {
  return (
    <div className="tuition-conversion-summary" data-testid={`tuition-conversion-${summary.invoiceItemId}`}>
      {showStudent && (
        <strong>
          {summary.studentName}
          {summary.daysPerWeek ? t('billing.tuitionAutofill.daysPerWeek', { count: summary.daysPerWeek }) : ''}
        </strong>
      )}
      <div>
        <strong>{t('billing.tuitionAutofill.beforeLabel', { defaultValue: 'Before:' })}</strong> {summary.before}
      </div>
      <div>
        <strong>
          {tuitionSettingsUrl ? (
            <a className="underline" href={tuitionSettingsUrl} target="_blank" rel="noreferrer">
              {t('billing.tuitionAutofill.tuitionSettingsLabel', { defaultValue: 'Tuition Settings:' })}
            </a>
          ) : (
            t('billing.tuitionAutofill.tuitionSettingsLabel', { defaultValue: 'Tuition Settings:' })
          )}
        </strong>{' '}
        {getTuitionSettingValue(summary, billingInterval, t)}
      </div>
      <div>
        <strong>{t('billing.tuitionAutofill.calculationUsedLabel', { defaultValue: 'Calculation Used:' })}</strong>{' '}
        {summary.calculation}
      </div>
      <div>
        <strong>{t('billing.tuitionAutofill.afterLabel', { defaultValue: 'After:' })}</strong> {summary.after}
      </div>
      {summary.hasTuitionSetting && (
        <div>
          <strong>{t('billing.tuitionAutofill.differenceLabel', { defaultValue: 'Difference:' })}</strong>{' '}
          {summary.differenceCalculation}
        </div>
      )}
    </div>
  );
}

function getTuitionSettingValue(summary, billingInterval, t) {
  if (summary.tuitionSettingState === 'configured') return summary.tuitionSettingAmount;

  const values = {
    empty: ['billing.tuitionAutofill.emptySettings', 'No tuition programs configured'],
    missingRate: ['billing.tuitionAutofill.noRate', `No ${billingInterval} Rate`],
    noMatchingProgram: ['billing.tuitionAutofill.noMatchingProgram', 'No matching tuition program'],
    unavailable: ['billing.tuitionAutofill.settingsUnavailable', 'Tuition Settings unavailable'],
    zeroRate: ['billing.tuitionAutofill.zeroRate', `${billingInterval} Rate is $0`],
  };
  const [translationKey, defaultValue] = values[summary.tuitionSettingState] ?? values.unavailable;

  return t(translationKey, { billingInterval, defaultValue });
}

function FinancialSummary({ financialSummary, isManualOverride, t }) {
  const money = (amount) =>
    `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="mt-3" data-testid="tuition-financial-summary">
      {isManualOverride && (
        <strong>{t('billing.tuitionAutofill.currentEditedResult', { defaultValue: 'Current edited result' })}</strong>
      )}
      <div>
        <strong>{t('billing.tuitionAutofill.perInvoiceSummary', { defaultValue: 'Per invoice:' })}</strong>{' '}
        {t('billing.tuitionAutofill.grossAndNetChange', {
          defaultValue: 'gross {{beforeGross}} → {{afterGross}}, net {{beforeNet}} → {{afterNet}}',
          beforeGross: money(financialSummary.before.perInvoiceGross),
          afterGross: money(financialSummary.after.perInvoiceGross),
          beforeNet: money(financialSummary.before.perInvoiceNet),
          afterNet: money(financialSummary.after.perInvoiceNet),
        })}
      </div>
      <div>
        <strong>{t('billing.tuitionAutofill.annualizedSummary', { defaultValue: 'Annualized:' })}</strong>{' '}
        {t('billing.tuitionAutofill.grossAndNetChange', {
          defaultValue: 'gross {{beforeGross}} → {{afterGross}}, net {{beforeNet}} → {{afterNet}}',
          beforeGross: money(financialSummary.before.annualGross),
          afterGross: money(financialSummary.after.annualGross),
          beforeNet: money(financialSummary.before.annualNet),
          afterNet: money(financialSummary.after.annualNet),
        })}
      </div>
      <div>
        {t('billing.tuitionAutofill.signedAnnualDeltas', {
          defaultValue: 'Signed annual change: gross {{gross}}, net {{net}}',
          gross: signedMoney(financialSummary.annualGrossDelta),
          net: signedMoney(financialSummary.annualNetDelta),
        })}
      </div>
      {financialSummary.components.map((component) => (
        <div key={component.id}>
          {component.label}:{' '}
          {isManualOverride
            ? t('billing.tuitionAutofill.editedComponentDelta', {
                defaultValue: 'current edited annual change {{delta}}',
                delta: signedMoney(component.annualDrift),
              })
            : `${signedMoney(component.annualDrift)} (${t('billing.tuitionAutofill.roundingTolerance', {
                defaultValue: 'rounding drift tolerance ±{{tolerance}}',
                tolerance: money(component.tolerance),
              })})`}
        </div>
      ))}
    </div>
  );

  function signedMoney(amount) {
    if (amount === 0) return money(0);
    return `${amount > 0 ? '+' : '−'}${money(Math.abs(amount))}`;
  }
}

function ConversionGuidance({ t, tuitionSettingsUrl }) {
  return (
    <div className="mt-3">
      {t('billing.tuitionAutofill.changeIntervalBeforeLink', { defaultValue: 'Change the billing interval in ' })}
      <Link
        className="underline"
        data-testid="tuition-details-math-link"
        to={`${routes.billing.plans.edit}${routes.billing.hashes.details}`}
      >
        {t('billing.tuitionAutofill.detailsLink')}
      </Link>
      {t('billing.tuitionAutofill.changeIntervalBetweenLinks', { defaultValue: ' or update rates in ' })}
      {tuitionSettingsUrl ? (
        <a
          className="underline"
          data-testid="tuition-settings-math-link"
          href={tuitionSettingsUrl}
          target="_blank"
          rel="noreferrer"
        >
          {t('billing.tuitionAutofill.tuitionSettingsLink')}
        </a>
      ) : (
        t('billing.tuitionAutofill.tuitionSettingsLink')
      )}
      {t('billing.tuitionAutofill.editableNoteAfterLink', { defaultValue: '.' })}
    </div>
  );
}
